<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <card :title="$t('holidays.title')" :on-add-object="openCreate">
          <div class="d-flex align-items-center gap-2 mb-3">
            <el-button size="small" @click="changeYear(-1)"><i class="material-icons" style="font-size:1rem">chevron_left</i></el-button>
            <span class="fw-bold">{{ year }}</span>
            <el-button size="small" @click="changeYear(1)"><i class="material-icons" style="font-size:1rem">chevron_right</i></el-button>
          </div>

          <div v-if="!loaded" class="text-center text-secondary py-4">{{ $t('common.loading') }}</div>
          <el-table v-else :data="holidays" row-key="date" style="width:100%">
            <el-table-column :label="$t('timeRegistration.date')" width="130">
              <template #default="scope">{{ formatDate(scope.row.date) }}</template>
            </el-table-column>
            <el-table-column :label="$t('holidays.name')" prop="name" min-width="180" />
            <el-table-column :label="$t('holidays.typeColumn')" width="220">
              <template #default="scope">
                <el-tag v-if="scope.row.isMajorHoliday" type="danger" size="small">{{ $t('holidays.majorHoliday') }}</el-tag>
                <el-tag v-else type="warning" size="small">{{ $t('holidays.generalHoliday') }}</el-tag>
                <el-tag v-if="scope.row.isHalfDay" type="info" size="small" class="ms-1">{{ $t('holidays.halfDay') }}</el-tag>
                <el-tag v-if="scope.row.isCustom" type="success" size="small" class="ms-1">{{ $t('holidays.customDay') }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="" width="90" align="right">
              <template #default="scope">
                <el-button v-if="scope.row.isCustom" size="small" type="danger" @click="confirmDelete(scope.row)">
                  <i class="material-icons" style="font-size:1rem">delete</i>
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </card>
      </div>
    </div>

    <el-dialog v-model="showForm" :title="$t('holidays.newHoliday')" width="480">
      <div class="mb-3">
        <label class="form-label">{{ $t('timeRegistration.date') }} <span class="text-danger">*</span></label>
        <input v-model="form.date" type="date" class="form-control" />
      </div>
      <div class="mb-3">
        <label class="form-label">{{ $t('holidays.name') }} <span class="text-danger">*</span></label>
        <el-input v-model="form.name" maxlength="200" />
      </div>
      <div class="mb-2">
        <el-checkbox v-model="form.isMajorHoliday">{{ $t('holidays.majorHoliday') }}</el-checkbox>
      </div>
      <template #footer>
        <el-button @click="showForm = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" :loading="saving" :disabled="!form.date || !form.name" @click="submitForm">{{ $t('common.submit') }}</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import api from '../../services/api';
import Card from '../../components/Card.vue';
import { ElDialog, ElInput, ElButton, ElTable, ElTableColumn, ElTag, ElCheckbox, ElMessageBox } from 'element-plus';

/**
 * ManageHolidays — Admin view of the holiday calendar for a year.
 * Icelandic public holidays are computed server-side (read-only);
 * customer-defined extra days can be added and deleted.
 */
export default {
  name: 'ManageHolidays',
  components: {
    Card,
    ElDialog, ElInput, ElButton, ElTable, ElTableColumn, ElTag, ElCheckbox,
  },
  data() {
    return {
      loaded: false,
      year: new Date().getFullYear(),
      holidays: [],
      showForm: false,
      saving: false,
      form: { date: '', name: '', isMajorHoliday: false },
    };
  },
  created() {
    this.fetchHolidays();
  },
  methods: {
    async fetchHolidays() {
      this.loaded = false;
      try {
        const res = await api.get(`/Holiday?year=${this.year}`);
        this.holidays = res.data || [];
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
        this.$toast.danger(this.$t('common.unableToRetrieveData'), { timeout: 0 });
      } finally {
        this.loaded = true;
      }
    },
    changeYear(delta) {
      this.year += delta;
      this.fetchHolidays();
    },
    formatDate(dt) {
      const d = new Date(dt);
      return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
    },
    openCreate() {
      this.form = { date: '', name: '', isMajorHoliday: false };
      this.showForm = true;
    },
    async submitForm() {
      this.saving = true;
      try {
        await api.post('/Holiday', {
          date: this.form.date,
          name: this.form.name,
          isMajorHoliday: this.form.isMajorHoliday,
        });
        this.showForm = false;
        await this.fetchHolidays();
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
        this.$toast.danger(err?.response?.data || this.$t('common.unableToUpdate'), { timeout: 0 });
      } finally {
        this.saving = false;
      }
    },
    async confirmDelete(row) {
      try {
        await ElMessageBox.confirm(this.$t('holidays.deleteConfirm'), row.name, {
          confirmButtonText: this.$t('common.delete'),
          cancelButtonText: this.$t('common.cancel'),
          type: 'warning',
        });
      } catch {
        return;
      }
      try {
        await api.delete(`/Holiday/${row.id}`);
        await this.fetchHolidays();
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
        this.$toast.danger(this.$t('common.unableToUpdate'), { timeout: 0 });
      }
    },
  },
};
</script>
